import { NavLink } from 'react-router-dom';
import {
  Briefcase,
  FileText,
  MessageSquare,
  Wallet,
  User,
  Shield,
  Settings,
  LayoutDashboard,
  Users,
  ClipboardCheck,
  DollarSign,
  ScrollText,
  Ban,
  Flame,
} from 'lucide-react';
import { Role } from '@hirenfire/shared';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/cn';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  roles?: Role[];
}

const navItems: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
  { to: '/jobs', label: 'Jobs', icon: <Briefcase className="w-5 h-5" /> },
  {
    to: '/applications',
    label: 'Applications',
    icon: <FileText className="w-5 h-5" />,
    roles: [Role.WORKER],
  },
  { to: '/contracts', label: 'Contracts', icon: <ScrollText className="w-5 h-5" /> },
  { to: '/chat', label: 'Chat', icon: <MessageSquare className="w-5 h-5" /> },
  { to: '/payments', label: 'Payments', icon: <DollarSign className="w-5 h-5" /> },
  { to: '/wallet', label: 'Wallet', icon: <Wallet className="w-5 h-5" /> },
  { to: '/profile/me', label: 'Profile', icon: <User className="w-5 h-5" /> },
  { to: '/kyc', label: 'Verification', icon: <Shield className="w-5 h-5" /> },
  { to: '/settings', label: 'Settings', icon: <Settings className="w-5 h-5" /> },
];

const adminItems: NavItem[] = [
  { to: '/admin/users', label: 'Users', icon: <Users className="w-5 h-5" />, roles: [Role.ADMIN] },
  { to: '/admin/kyc', label: 'KYC Reviews', icon: <ClipboardCheck className="w-5 h-5" />, roles: [Role.ADMIN] },
  { to: '/admin/escrow', label: 'Escrow', icon: <DollarSign className="w-5 h-5" />, roles: [Role.ADMIN] },
  { to: '/admin/audit', label: 'Audit Log', icon: <ScrollText className="w-5 h-5" />, roles: [Role.ADMIN] },
  { to: '/admin/bans', label: 'Bans', icon: <Ban className="w-5 h-5" />, roles: [Role.ADMIN] },
];

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const role = user?.role;

  const filteredItems = navItems.filter(
    (item) => !item.roles || (role && item.roles.includes(role))
  );

  const filteredAdminItems = adminItems.filter(
    (item) => !item.roles || (role && item.roles.includes(role))
  );

  return (
    <aside className="fixed left-0 top-0 h-screen w-64 bg-dark-900 border-r border-dark-600 flex flex-col z-40">
      <div className="flex items-center gap-2 px-6 py-5 border-b border-dark-600">
        <Flame className="w-7 h-7 text-primary-500" />
        <span className="text-xl font-bold gradient-text">Hirenfire</span>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {filteredItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(isActive ? 'sidebar-link-active' : 'sidebar-link')
            }
          >
            {item.icon}
            <span>{item.label}</span>
          </NavLink>
        ))}

        {filteredAdminItems.length > 0 && (
          <>
            <div className="pt-4 pb-2 px-4">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Admin
              </span>
            </div>
            {filteredAdminItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(isActive ? 'sidebar-link-active' : 'sidebar-link')
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="px-4 py-4 border-t border-dark-600">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 text-sm font-semibold">
            {user?.profile?.displayName?.[0]?.toUpperCase() ?? 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {user?.profile?.displayName ?? 'User'}
            </p>
            <p className="text-xs text-gray-500 capitalize">{role?.toLowerCase()}</p>
          </div>
        </div>
      </div>
    </aside>
  );
}
